import { ApiMethod } from "../models/api-types.type";
import AuthClientStore from "../services/auth-client.service";
import { useApi } from "./use-api.hook";
import type { BusyRange } from '../../modules/signup/demo-slots';

/**
 * The self-serve signup ("Start for free") and the activation step after it.
 * Contract: docs/plg-free-plan.md. The public calls go out with no token; the
 * status/install calls are made as the newly signed-in vendor.
 */
export const SIGNUP_ROUTES = {
    config: "/signup/config",
    challenge: "/signup/challenge",
    website: "/signup/website",
    register: "/signup/register",
    google: "/signup/google",
    googleSignIn: "/signup/google/signin",
    status: "/services/pms/signup/status",
    checkInstall: "/services/pms/signup/check-install",
    resendVerification: "/services/pms/signup/resend-verification",
    sendToDeveloper: "/services/pms/signup/send-to-developer",
    /** Public, and also accepted with a backoffice token (docs/plg-review.md). */
    demo: "/signup/demo",
    /** Public: the times already booked for a demo call, nothing about who booked them (ARDE-288). */
    demoBusy: "/signup/demo/busy",
} as const;

export interface SignupConfig {
    googleClientId: string | null;
    turnstileSiteKey: string | null;
    powDifficulty?: number;
    /** The server refuses a puzzle spent sooner than this after it was issued. */
    minFillMs?: number;
    /** How long a review usually takes, in working hours. */
    reviewSlaHours?: number;
    /** A Cal.com / Calendly link for "Pick a time now"; null when none is set up. */
    demoBookingUrl?: string | null;
}

/** Where a visitor came from, read once from the `/start` URL and the referrer. */
export interface SignupSource {
    referrer?: string;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_term?: string;
    utm_content?: string;
    /** Google Ads click id: which ad and keyword brought this visitor. */
    gclid?: string;
}

/** Sent on every public signup call so the API can join what happened before an account existed. */
export interface JourneyFields {
    journeyId?: string;
    source?: SignupSource;
}

/** A proof-of-work puzzle: find `nonce` so that sha256(salt + nonce) starts with `difficulty` zero bits. */
export interface SignupChallenge {
    id: string;
    salt: string;
    difficulty: number;
    expiresAt: string | number;
    signature: string;
}

export type SolvedChallenge = SignupChallenge & { nonce: string };

export interface WebsiteCheck {
    domain: string;
    available: boolean;
    reachable: boolean;
    title?: string;
    platform?: string;
}

/** Either a solved puzzle or a Turnstile token — whichever the server asked for. */
export type HumanProof = { challenge?: SolvedChallenge; turnstileToken?: string };

export interface RegisterBody {
    website: string;
    email: string;
    password: string;
    firstname?: string;
    lastname?: string;
    company?: string;
    locale?: string;
    /** The honeypot: people never see it, so it stays empty. */
    company_url: string;
    startedAt: number;
}

export type DemoSells = 'online' | 'offline' | 'both';
export type DemoReason = 'offline_store' | 'cant_install_now' | 'want_walkthrough' | 'other';

export interface DemoRequestBody extends JourneyFields {
    name: string;
    email: string;
    phone?: string;
    company?: string;
    website?: string;
    sells: DemoSells;
    reason: DemoReason;
    message?: string;
    /** The call time picked (ISO 8601); staff send a Meet invite for it. */
    preferredAt: string;
    /** The visitor's IANA time zone, to read the time back to them. */
    timeZone?: string;
    locale?: string;
    /** The honeypot, as on the signup form. */
    company_url: string;
    /** Booked from a prospect's demo (<slug>.ardealy.link): the API ties the request to that prospect. */
    demoSlug?: string;
    startedAt?: number;
}

export interface DemoResponse {
    ok: boolean;
    bookingUrl: string | null;
}

export interface SignedInTokens {
    access_token: string;
    refresh_token: string;
    user?: any;
}

export type SignupReviewReason = 'no_business' | 'unrelated_business' | 'duplicate' | 'fake_details' | 'other';

export interface SignupStatus {
    selfServe: boolean;
    /** pending_embed → pending_review (both milestones met) → active, or blocked. */
    status: 'pending_embed' | 'pending_review' | 'blocked' | 'active' | string;
    emailVerified: boolean;
    sdkConnected: boolean;
    domain?: string;
    alias?: string;
    snippet?: string;
    lastCheck?: { at: string; found: boolean; reason?: string };
    /** What the vendor may see of the review: never the internal note or who decided. */
    review?: { status: 'pending' | 'approved' | 'blocked'; decidedAt?: string; reason?: SignupReviewReason | string; message?: string };
    reviewSlaHours?: number;
    demoRequested?: boolean;
}

export interface InstallCheck {
    found?: boolean;
    reason?: string;
    at?: string;
    status?: SignupStatus['status'];
    sdkConnected?: boolean;
}

export const useSignupApi = () => {
    const { sendRequest, sendProtectedRequest } = useApi();

    return {
        config: (): Promise<SignupConfig> => sendRequest(ApiMethod.GET, SIGNUP_ROUTES.config),
        challenge: (): Promise<SignupChallenge> => sendRequest(ApiMethod.GET, SIGNUP_ROUTES.challenge),
        demoBusy: (from: string, to: string): Promise<{ busy: BusyRange[] }> =>
            sendRequest(ApiMethod.GET, `${SIGNUP_ROUTES.demoBusy}?${new URLSearchParams({ from, to })}`),
        checkWebsite: (website: string, journey: JourneyFields = {}): Promise<WebsiteCheck> =>
            sendRequest(ApiMethod.POST, SIGNUP_ROUTES.website, { website, ...journey }),
        register: (body: RegisterBody & HumanProof & JourneyFields): Promise<SignedInTokens> => sendRequest(ApiMethod.POST, SIGNUP_ROUTES.register, body),
        google: (
            body: { credential: string; website: string; locale?: string; challenge?: SolvedChallenge; turnstileToken?: string; company_url?: string; startedAt?: number } & JourneyFields,
        ): Promise<SignedInTokens> => sendRequest(ApiMethod.POST, SIGNUP_ROUTES.google, body),
        /** "Continue with Google" on the sign-in page: an existing account only (NO_ACCOUNT otherwise). */
        googleSignIn: (credential: string): Promise<SignedInTokens> => sendRequest(ApiMethod.POST, SIGNUP_ROUTES.googleSignIn, { credential }),
        /**
         * "Book a demo". Signed out it goes with the human check; signed in the
         * token goes along instead, so the request is linked to the vendor.
         */
        requestDemo: (body: DemoRequestBody & HumanProof, signedIn = false): Promise<DemoResponse> =>
            sendRequest(ApiMethod.POST, SIGNUP_ROUTES.demo, body, signedIn ? AuthClientStore.getAccessToken() : null),

        // Signed in as the new vendor.
        status: (): Promise<SignupStatus> => sendProtectedRequest(ApiMethod.GET, SIGNUP_ROUTES.status),
        checkInstall: (): Promise<InstallCheck> => sendProtectedRequest(ApiMethod.POST, SIGNUP_ROUTES.checkInstall),
        resendVerification: () => sendProtectedRequest(ApiMethod.POST, SIGNUP_ROUTES.resendVerification),
        sendToDeveloper: (email: string) => sendProtectedRequest(ApiMethod.POST, SIGNUP_ROUTES.sendToDeveloper, { email }),
    };
};
